import { FC, useState, useMemo, lazy, Suspense, useEffect } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Alert,
  Badge,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Chip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import HistoryIcon from "@mui/icons-material/History";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ScienceIcon from "@mui/icons-material/Science";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { CopilotTab } from "./types";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { useCopilotChat } from "./useCopilotChat";
import { useQuickGuide } from "../../features/guide/useQuickGuide";
import {
  ChatMessage,
  TestAccountInfo,
} from "../../features/copilot/copilot.api";
import { trackEvent } from "../../features/analytics/analytics";
import { flushIfQueued } from "../../features/analytics/analyticsBatcher";
import { useAtomValue } from "jotai";
import { conversationsAtom } from "../../features/copilot/copilot.atoms";

// Component to fetch last conversation with Suspense isolation
const LastConversationFetcher: FC<{
  onLastConversation: (conv) => void;
}> = ({ onLastConversation }) => {
  const conversations = useAtomValue(conversationsAtom);
  const lastConversation = conversations.length > 0 ? conversations[0] : null;

  // Notify parent of last conversation (only on changes)
  useEffect(() => {
    onLastConversation(lastConversation);
  }, [lastConversation, onLastConversation]);

  return null; // No UI, just data fetching
};

// Lazy load heavy components to prevent flickering
const ConversationHistory = lazy(() =>
  import("./ConversationHistory").then((module) => ({
    default: module.ConversationHistory,
  })),
);

const StepsPanel = lazy(() =>
  import("./StepsPanel").then((module) => ({ default: module.StepsPanel })),
);

interface CopilotWidgetProps {
  size?: "compact" | "embedded" | "fullscreen";
  showFeedback?: boolean;
  defaultTab?: CopilotTab;
  height?: string | number;
  // Optional: Pass chat hook from parent (for Lab integration)
  messages?: ChatMessage[];
  loading?: boolean;
  error?: string | null;
  conversationId?: number;
  onSendMessage?: (message: string) => Promise<boolean>;
  onSendFeedback?: (rating: number, comment?: string) => Promise<void>;
  onReset?: () => void;
  onLoadConversation?: (id: number) => Promise<void>;
  // Lab: Test account info for banner
  testAccountInfo?: TestAccountInfo;
  // Lab: Show view mode toggle (markdown/rich)
  showViewModeToggle?: boolean;
  // Hide bottom tab bar (for embedded minimal mode like onboarding)
  hideTabs?: boolean;
  // Hide "new chat" reset button in input area
  hideNewChat?: boolean;
  // Lab/Modal: Loaded conversation user info
  conversationUserInfo?: {
    userEmail: string;
    accountId: number;
    accountNamespace: string;
    businessName?: string;
    status?: string;
    resolutionStatus?: string;
  };
}

export const CopilotWidget: FC<CopilotWidgetProps> = ({
  size = "fullscreen",
  showFeedback = true,
  defaultTab = "conversation",
  height = "100%",
  // Injected props from parent (Lab)
  messages: externalMessages,
  loading: externalLoading,
  error: externalError,
  conversationId: externalConversationId,
  onSendMessage,
  onSendFeedback,
  onReset,
  onLoadConversation,
  testAccountInfo,
  showViewModeToggle = false,
  hideTabs = false,
  hideNewChat = false,
  conversationUserInfo,
}) => {
  const [activeTab, setActiveTab] = useState<CopilotTab>(defaultTab);
  const [viewMode, setViewMode] = useState<"markdown" | "rich">("rich");

  // Use internal hook only if external props not provided (standalone mode)
  const internalHook = useCopilotChat();

  // State for last conversation (updated by Suspense-wrapped component)
  const [lastConversation, setLastConversation] = useState(null);

  // State for last conversation banner
  const [showBanner, setShowBanner] = useState(false);

  // Flush analytics on mount if queue has enough events (5+)
  useEffect(() => {
    flushIfQueued("copilot-open", 5);
  }, []); // Run once on mount

  // Get guide steps for badge count (use all steps, not filtered by hideCompleted)
  const { allGuideSteps } = useQuickGuide();
  const completedCount = useMemo(
    () => allGuideSteps.filter((step) => step.completed).length,
    [allGuideSteps],
  );
  const totalCount = allGuideSteps.length;
  const stepsLabel = `${completedCount}/${totalCount}`;

  // Use external values if provided, otherwise use internal hook
  const messages = externalMessages ?? internalHook.messages;
  const loading = externalLoading ?? internalHook.loading;
  const error = externalError ?? internalHook.error;
  const conversationId = externalConversationId ?? internalHook.conversationId;
  const sendMessage = onSendMessage ?? internalHook.sendMessage;
  const sendFeedback = onSendFeedback ?? internalHook.sendFeedback;
  const reset = onReset ?? internalHook.reset;
  const loadConversation = onLoadConversation ?? internalHook.loadConversation;

  // Show banner for continuing last conversation
  useEffect(() => {
    // Only show banner if no current messages and we have a last conversation
    if (messages.length === 0 && !loading && lastConversation) {
      setShowBanner(true);
    } else if (messages.length > 0) {
      setShowBanner(false);
    }
  }, [messages.length, loading, lastConversation]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: CopilotTab,
  ) => {
    // Track tab switch
    trackEvent({
      type: "trace",
      category: "ai-copilot",
      action: "ai-help-tab-switch",
      label: newValue,
      metadata: {
        from: activeTab,
        to: newValue,
      },
      internal: true,
    });

    setActiveTab(newValue);
  };

  const handleSelectConversation = async (id: number) => {
    await loadConversation(id);
    setActiveTab("conversation");
  };

  const handleNewConversation = () => {
    reset();
    setActiveTab("conversation");
  };

  // Size-specific dimensions
  const getWidgetHeight = () => {
    //if (size === "compact") return "600px";
    if (size === "embedded") return height;
    return height;
  };

  const getWidgetWidth = () => {
    if (size === "compact") return "600px";
    return "100%";
  };

  return (
    <Paper
      elevation={size === "compact" ? 4 : 2}
      sx={{
        height: getWidgetHeight(),
        width: getWidgetWidth(),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fetch last conversation with Suspense isolation (no UI, just data) */}
      <Suspense fallback={null}>
        <LastConversationFetcher onLastConversation={setLastConversation} />
      </Suspense>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Test Mode Banner */}
        {testAccountInfo && (
          <Alert
            severity="info"
            icon={<ScienceIcon />}
            sx={{
              borderRadius: 0,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            🧪 Test Mode: Using context from account #
            {testAccountInfo.accountId} - {testAccountInfo.namespace}
          </Alert>
        )}

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {activeTab === "conversation" && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* View Mode Toggle */}
              {showViewModeToggle && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  {conversationUserInfo ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {conversationUserInfo.userEmail} | Account #
                        {conversationUserInfo.accountId} (
                        {conversationUserInfo.accountNamespace})
                        {conversationUserInfo.businessName &&
                          ` - ${conversationUserInfo.businessName}`}
                      </Typography>
                      {conversationUserInfo.status && (
                        <Chip
                          label={conversationUserInfo.status}
                          size="small"
                          color={
                            conversationUserInfo.status === "active"
                              ? "success"
                              : "default"
                          }
                        />
                      )}
                      {conversationUserInfo.resolutionStatus && (
                        <Chip
                          label={conversationUserInfo.resolutionStatus.replace(
                            "_",
                            " ",
                          )}
                          size="small"
                          color={
                            conversationUserInfo.resolutionStatus === "helpful"
                              ? "success"
                              : conversationUserInfo.resolutionStatus ===
                                  "not_helpful"
                                ? "error"
                                : "warning"
                          }
                        />
                      )}
                    </Box>
                  ) : (
                    <Box />
                  )}
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_e, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                  >
                    <ToggleButton value="markdown">Markdown</ToggleButton>
                    <ToggleButton value="rich">Rich</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Continue Last Conversation Banner */}
              {showBanner && lastConversation && messages.length === 0 && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 0,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                  onClick={() => {
                    loadConversation(lastConversation.id);
                    setShowBanner(false);
                  }}
                  onClose={() => setShowBanner(false)}
                >
                  Continue:{" "}
                  {lastConversation.title ||
                    `Conversation #${lastConversation.id}`}
                </Alert>
              )}

              {/* Messages */}
              <MessageList
                messages={messages}
                viewMode={viewMode}
                onFeedback={showFeedback ? sendFeedback : undefined}
              />

              {/* Error */}
              {error && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: "error.light",
                    color: "error.contrastText",
                  }}
                >
                  {error}
                </Box>
              )}

              {/* Input */}
              <MessageInput
                onSend={sendMessage}
                loading={loading}
                autoFocus={true}
                onNewChat={hideNewChat ? undefined : reset}
                hasMessages={messages.length > 0}
              />
            </Box>
          )}

          {activeTab === "history" && (
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <ConversationHistory
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                currentConversationId={conversationId}
                testAccountId={testAccountInfo?.accountId}
              />
            </Suspense>
          )}

          {activeTab === "steps" && (
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <StepsPanel />
            </Suspense>
          )}

          {activeTab === "more" && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Box sx={{ color: "text.secondary", mb: 2 }}>
                <MoreHorizIcon sx={{ fontSize: 48 }} />
              </Box>
              <Box sx={{ color: "text.secondary" }}>
                Additional features coming soon:
                <br />
                Articles, FAQs, Prompt Templates
              </Box>
            </Box>
          )}
        </Box>

        {/* Bottom Tab Navigation */}
        {!hideTabs && <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{
            borderTop: 1,
            borderColor: "divider",
            minHeight: 64,
            "& .MuiTab-root": {
              minHeight: 64,
              fontSize: "0.7rem",
              minWidth: 0,
              px: 2, // More spacing for badge
            },
          }}
        >
          <Tab
            label="Chat"
            value="conversation"
            icon={<ChatIcon fontSize="small" />}
            iconPosition="top"
          />
          <Tab
            label="History"
            value="history"
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="top"
          />
          <Tab
            label={
              <Box sx={{ position: "relative", display: "inline-block" }}>
                Steps
                <Badge
                  badgeContent={stepsLabel}
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: -8,
                    //right: -4,
                    "& .MuiBadge-badge": {
                      fontSize: "0.6rem",
                      minWidth: 20,
                      height: 16,
                      padding: "0 4px",
                    },
                  }}
                />
              </Box>
            }
            value="steps"
            icon={<FormatListNumberedIcon fontSize="small" />}
            iconPosition="top"
          />
          {/* <Tab
            label="More"
            value="more"
            icon={<MoreHorizIcon fontSize="small" />}
            iconPosition="top"
          /> */}
        </Tabs>}
      </Box>
    </Paper>
  );
};
